import type { MailboxInfo } from 'owa-client-types';
import {
    type ImplementedCopilotFeatures,
    type CopilotSettingsStore,
    shouldDisableCopilot,
} from 'owa-copilot-settings-store';
import { getApplicationSettings } from 'owa-application-settings';
import checkLanguage from './checkLanguage';
import createStoreCheckForGenericEnablement from './createStoreCheckForGenericEnablement';
import { isFeatureEnabled, isAccountFeatureEnabled } from 'owa-feature-flags';
import {
    getCoprincipalMailboxInfo,
    getGlobalSettingsAccountMailboxInfo,
} from 'owa-account-source-list-store';
import isPrioritizeEnabled from './isPrioritizeEnabled';
import { getIsCopilotUIDisabled } from 'owa-mail-copilot-settings/lib/selectors/getIsCopilotUIDisabled';
import type { CopilotEnablementDiagnostics } from 'owa-copilot-settings-store/lib/utils/enablementCodes/CopilotEnablementTypes';
import { CopilotFeatureEnablementCodes } from 'owa-copilot-settings-store/lib/utils/enablementCodes/CopilotFeatureEnablementCodes';

const isCopilotFeatureEnabled: (
    scenario: ImplementedCopilotFeatures,
    mailboxInfo: MailboxInfo,
    skipLanguageCheck?: boolean,
    shouldIgnoreUserPreferences?: boolean,
    diagnostics?: CopilotEnablementDiagnostics
) => boolean | undefined =
    // eslint-disable-next-line owa-custom-rules/no-side-effects -- BASELINE, DO NOT COPY-PASTE
    createStoreCheckForGenericEnablement<ImplementedCopilotFeatures, boolean>(
        'CopilotEnabled',
        isCopilotFeatureEnabledInternal
    );

export default isCopilotFeatureEnabled;

function isCopilotFeatureEnabledInternal(
    store: CopilotSettingsStore,
    language: string,
    scenario: ImplementedCopilotFeatures,
    mailboxInfo: MailboxInfo,
    skipLanguageCheck?: boolean,
    shouldIgnoreUserPreferences?: boolean,
    diagnostics?: CopilotEnablementDiagnostics
) {
    // We also need to have a check here as boot cache will bypass fetchCopilotSettings and directly dump settings into store.
    if (shouldDisableCopilot(mailboxInfo, diagnostics)) {
        return false;
    }

    if (
        isFeatureEnabled('mon-copilot-user-preferences') &&
        !shouldIgnoreUserPreferences &&
        getIsCopilotUIDisabled(mailboxInfo)
    ) {
        diagnostics &&
            (diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_DISABLED_USER_SETTING);
        return false;
    }

    if (!store.isEnabled) {
        diagnostics && (diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_STORE_NOT_ENABLED);
        return false;
    }

    const featureEnabled = store.featureInfo.get(scenario);
    if (featureEnabled?.isEnabled) {
        const coprincipalMailboxInfo =
            mailboxInfo.type === 'GroupMailbox'
                ? getCoprincipalMailboxInfo(mailboxInfo) ?? getGlobalSettingsAccountMailboxInfo()
                : mailboxInfo;
        const enabledInECS = isEnabledInECS(scenario, coprincipalMailboxInfo);
        const languageSupported =
            skipLanguageCheck ||
            language === 'qps-ploc' ||
            checkLanguage(language, featureEnabled.langauges);

        if (diagnostics) {
            if (!enabledInECS) {
                diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_DISABLED_ECS;
            } else if (!languageSupported) {
                diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_UNSUPPORTED_LANGUAGE;
            }
        }

        return enabledInECS && languageSupported;
    }
    diagnostics &&
        (diagnostics.code = CopilotFeatureEnablementCodes.COPILOT_STORE_FEATURE_NOT_ENABLED);
    return false;
}

function isEnabledInECS(scenario: ImplementedCopilotFeatures, mailboxInfo: MailboxInfo): boolean {
    // retrieve application settings for the specific feature as a small performance optimization
    switch (scenario) {
        case 'AttachmentSummarization':
            return getApplicationSettings('Copilot', mailboxInfo).attachmentSummarizationEnabled;
        case 'Elaborate':
            return getApplicationSettings('Copilot', mailboxInfo).elaborateEnabled;
        case 'Summarize':
            return getApplicationSettings('Copilot', mailboxInfo).summarizeEnabled;
        case 'Coach':
        case 'CoachApply':
            return getApplicationSettings('Copilot', mailboxInfo).coachEnabled;
        case 'SuggestedDrafts':
            return getApplicationSettings('Copilot', mailboxInfo).suggestedDraftsEnabled;
        case 'SuggestCategory':
            return isFeatureEnabled('time-copilot-categories-row-v2');
        case 'ExplainPlacesReport':
            return true; // client side feature flag.
        case 'Rewrite':
            return isFeatureEnabled('mon-copilot-elaborate-rewrite');
        case 'AgendaGeneration':
            return isFeatureEnabled('time-copilot-draft-intelligent-agenda');
        case 'MeetingPrepSummary':
            // Used by FetchCopilotSettings - Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1116121
            // getApplicationSettings('Copilot', mailboxInfo).meetingPrepSummaryEnabled - Client: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return getApplicationSettings('Copilot', mailboxInfo).meetingPrepSummaryEnabled;
        case 'ReplyByMeeting':
            // Used by FetchCopilotSettings - Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1123944
            // getApplicationSettings('Copilot', mailboxInfo).meetingPrepSummaryEnabled - Client: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return getApplicationSettings('Copilot', mailboxInfo).replyByMeetingEnabled;
        case 'Chat':
            // Used by FetchCopilotSettings - Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1098830
            return true; // Rely on license check for Chat.
        case 'Inbox':
            return isPrioritizeEnabled(mailboxInfo);
        case 'TimeCopilotChat':
            return getApplicationSettings('Copilot', mailboxInfo).timeCopilotChatEnabled;
        case 'VisualTheming':
            return isFeatureEnabled('fwk-copilot-themes');
        case 'MChat':
            return true; // Rely on license check for MChat.
        case 'AutoResourceBooking':
            // https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return getApplicationSettings('Copilot', mailboxInfo).autoResourceBookingEnabled;
        case 'BizChatHandoff':
            return isAccountFeatureEnabled('cmp-copilot-bizchat', mailboxInfo);
        case 'AttendeeColocation':
            // Service: https://ecs.skype.com/OlkCopilot/OlkCopilot/configurations/1569405
            // Client: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
            return getApplicationSettings('Copilot', mailboxInfo).attendeeColocationEnabled;
        case 'RSVPSuggestions':
            return isFeatureEnabled('time-copilot-rsvp-suggestions');
        case 'ManagedEvents':
            return isFeatureEnabled('time-copilot-managed-events');
        case 'PriorityCalendar':
            return true; // Rely on license check and dev flight for Priority Calendar.
        case 'CalendarInstructions':
            // Dev flight to disable CalendarInstructions for testing
            return !isFeatureEnabled('cal-ai-dev-disable-calendar-instructions');
        default:
            return false;
    }
}
